import React from 'react';

import { formatMoney } from '@betterment/core';
import { Box } from '@betterment/samba-react';

import { TaxImpactGainLossWashSaleWarning } from './TaxImpactGainLossWashSaleWarning';

import { DetailRow } from '../detail-row/DetailRow';
import { DetailRowLabel } from '../detail-row/DetailRowLabel';
import { DetailRowMoneyValue } from '../detail-row/DetailRowMoneyValue';
import { DetailRowTooltip } from '../detail-row/DetailRowTooltip';
import { DetailRowValue } from '../detail-row/DetailRowValue';
import { ExpandableDetail } from '../expandable-detail/ExpandableDetail';
import { ExpandableDetailContent } from '../expandable-detail/ExpandableDetailContent';
import { ExpandableDetailTrigger } from '../expandable-detail/ExpandableDetailTrigger';
import { GainLossData, GainLossDataWithWashSales } from '../types';

type TaxImpactGainLossLoadedPropsWithoutWashSales = {
  gainLoss: GainLossData;
  variant: 'withoutWashSales';
};

type TaxImpactGainLossLoadedPropsWithWashSales = {
  gainLoss: GainLossDataWithWashSales;
  variant: 'withWashSales';
};

export type TaxImpactGainLossLoadedProps =
  | TaxImpactGainLossLoadedPropsWithoutWashSales
  | TaxImpactGainLossLoadedPropsWithWashSales;

function willIncurLossesFromWashSales(preview: GainLossDataWithWashSales): boolean {
  return (
    preview.shortTermWashed.cents +
      preview.shortTermPermanentlyWashed.cents +
      preview.longTermWashed.cents +
      preview.longTermPermanentlyWashed.cents >
    0
  );
}

export const TaxImpactGainLossLoaded: React.FC<TaxImpactGainLossLoadedProps> = ({ gainLoss, variant }) => {
  const shouldShowWashSales = variant === 'withWashSales';
  const shouldShowWashSaleWarning = shouldShowWashSales && willIncurLossesFromWashSales(gainLoss);
  const netGainFormatted = formatMoney(gainLoss.netGain);

  return (
    <ExpandableDetail>
      <Box data-testid="gain-loss">
        <DetailRow>
          <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <DetailRowLabel>Est. realized gains/losses</DetailRowLabel>
            {shouldShowWashSaleWarning && <TaxImpactGainLossWashSaleWarning />}
          </Box>
          <DetailRowValue>
            {netGainFormatted}
            <ExpandableDetailTrigger
              css={{ mr: 'space.0', ml: 'auto' }}
              openText="Hide details"
              closedText="See details"
            />
          </DetailRowValue>
        </DetailRow>
        <ExpandableDetailContent>
          <DetailRow>
            <DetailRowLabel>Short term gain/loss</DetailRowLabel>
            <DetailRowMoneyValue value={gainLoss.netShortTerm} />
          </DetailRow>
          <DetailRow variant="weak">
            <DetailRowLabel>Short term gain</DetailRowLabel>
            <DetailRowMoneyValue value={gainLoss.shortTermGain} />
          </DetailRow>
          <DetailRow variant="weak">
            <DetailRowLabel>Short term loss</DetailRowLabel>
            <DetailRowMoneyValue value={gainLoss.shortTermLoss} />
          </DetailRow>
          {shouldShowWashSales && (
            <>
              <DetailRow variant="weak">
                <DetailRowLabel>
                  Short term washed loss
                  <DetailRowTooltip data-testid="short-term-washed-loss-tooltip">
                    Incurred amount from wash sales related to short term gains.
                  </DetailRowTooltip>
                </DetailRowLabel>
                <DetailRowMoneyValue value={gainLoss.shortTermWashed} />
              </DetailRow>
              <DetailRow variant="weak">
                <DetailRowLabel>Short term permanently washed loss</DetailRowLabel>
                <DetailRowMoneyValue value={gainLoss.shortTermPermanentlyWashed} />
              </DetailRow>
            </>
          )}
          <DetailRow>
            <DetailRowLabel>Long term gain/loss</DetailRowLabel>
            <DetailRowMoneyValue value={gainLoss.netLongTerm} />
          </DetailRow>
          <DetailRow variant="weak">
            <DetailRowLabel>Long term gain</DetailRowLabel>
            <DetailRowMoneyValue value={gainLoss.longTermGain} />
          </DetailRow>
          <DetailRow variant="weak">
            <DetailRowLabel>Long term loss</DetailRowLabel>
            <DetailRowMoneyValue value={gainLoss.longTermLoss} />
          </DetailRow>
          {shouldShowWashSales && (
            <>
              <DetailRow variant="weak">
                <DetailRowLabel>
                  Long term washed loss
                  <DetailRowTooltip data-testid="long-term-washed-loss-tooltip">
                    Incurred amount from wash sales related to long term gains.
                  </DetailRowTooltip>
                </DetailRowLabel>
                <DetailRowMoneyValue value={gainLoss.longTermWashed} />
              </DetailRow>
              <DetailRow variant="weak">
                <DetailRowLabel>Long term permanently washed loss</DetailRowLabel>
                <DetailRowMoneyValue value={gainLoss.longTermPermanentlyWashed} />
              </DetailRow>
            </>
          )}
        </ExpandableDetailContent>
      </Box>
    </ExpandableDetail>
  );
};
